import React, { useState, useEffect } from 'react';
import { Shield, Users, AlertTriangle, FileText, LogOut, Loader2, BarChart3, Settings, CheckCircle, XCircle, Globe, Trash2, Eye, EyeOff, Edit, Database, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({ exposure_checks: [], scam_reports: [], subscriptions: [], consultations: [], scam_analyses: [] });
  const [loading, setLoading] = useState(false);
  
  // Scam Entity Moderation
  const [scamEntities, setScamEntities] = useState([]);
  const [scamReportsV2, setScamReportsV2] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [entityFilter, setEntityFilter] = useState('all');
  const [reportFilter, setReportFilter] = useState('pending');
  const [editingEntity, setEditingEntity] = useState(null);
  const [entityStats, setEntityStats] = useState(null);
  
  // Threat Intelligence Settings
  const [threatIntelSettings, setThreatIntelSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    // NOTE: For production, admin tokens should use HTTP-only cookies set by backend.
    // Using sessionStorage as interim solution - token clears when browser tab closes.
    const savedToken = sessionStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStats();
      fetchData();
      if (activeTab === 'settings') {
        fetchThreatIntelSettings();
      }
      if (activeTab === 'entities') {
        fetchScamEntities();
        fetchEntityStats();
      }
      if (activeTab === 'reports') {
        fetchScamReportsV2();
      }
    }
  }, [isAuthenticated, token, activeTab, entityFilter, reportFilter]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      if (!response.ok) throw new Error('Invalid credentials');
      
      const data = await response.json();
      setToken(data.token);
      // NOTE: For production, use HTTP-only cookies. sessionStorage is interim solution.
      sessionStorage.setItem('admin_token', data.token);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Invalid username or password');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setToken('');
    setIsAuthenticated(false);
    setStats(null);
    setData({ exposure_checks: [], scam_reports: [], subscriptions: [], consultations: [], scam_analyses: [] });
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats?token=${token}`);
      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return;
        }
        throw new Error('Failed to fetch stats');
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'exposure': endpoint = 'exposure-checks'; break;
        case 'scams': endpoint = 'scam-reports'; break;
        case 'subscriptions': endpoint = 'subscriptions'; break;
        case 'consultations': endpoint = 'consultations'; break;
        case 'analyses': endpoint = 'scam-analyses'; break;
        default: setLoading(false); return;
      }

      const response = await fetch(`${API_URL}/api/admin/${endpoint}?token=${token}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      const dataKey = activeTab === 'exposure' ? 'exposure_checks' : 
                      activeTab === 'scams' ? 'scam_reports' : 
                      activeTab === 'analyses' ? 'scam_analyses' : activeTab;
      setData(prev => ({ ...prev, [dataKey]: result.data }));
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchThreatIntelSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/threat-intel-settings?token=${token}`);
      if (response.ok) {
        const settings = await response.json();
        setThreatIntelSettings(settings);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveThreatIntelSettings = async () => {
    setSettingsLoading(true);
    setSettingsSaved(false);
    try {
      const response = await fetch(`${API_URL}/api/admin/threat-intel-settings?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(threatIntelSettings)
      });
      if (response.ok) {
        setSettingsSaved(true);
        setTimeout(() => setSettingsSaved(false), 3000);
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  // ===================== SCAM ENTITY MODERATION =====================
  
  const fetchScamEntities = async () => {
    setEntitiesLoading(true);
    try {
      const statusParam = entityFilter === 'all' ? '' : `&status=${entityFilter}`;
      const response = await fetch(`${API_URL}/api/admin/scam-entities?token=${token}${statusParam}&limit=100`);
      if (response.ok) {
        const data = await response.json();
        setScamEntities(data.entities || []);
      }
    } catch (err) {
      console.error('Failed to fetch scam entities:', err);
    } finally {
      setEntitiesLoading(false);
    }
  };

  const fetchEntityStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scam-database/stats`);
      if (response.ok) {
        const data = await response.json();
        setEntityStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch entity stats:', err);
    }
  };

  const fetchScamReportsV2 = async () => {
    setReportsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/scam-reports-v2?token=${token}&status=${reportFilter}&limit=100`);
      if (response.ok) {
        const data = await response.json();
        setScamReportsV2(data.reports || []);
      }
    } catch (err) {
      console.error('Failed to fetch scam reports:', err);
    } finally {
      setReportsLoading(false);
    }
  };

  const updateEntity = async (slug, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/scam-entity/${slug}?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        toast.success('Entity updated successfully');
        fetchScamEntities();
        setEditingEntity(null);
      } else {
        toast.error('Failed to update entity');
      }
    } catch (err) {
      console.error('Failed to update entity:', err);
      toast.error('Failed to update entity');
    }
  };

  const togglePublished = async (entity) => {
    await updateEntity(entity.slug, { is_published: !entity.is_published });
  };

  const toggleVerifiedScam = async (entity) => {
    await updateEntity(entity.slug, { verified_scam: !entity.verified_scam });
  };

  const deleteEntity = async (slug) => {
    if (!window.confirm('Are you sure you want to remove this entity? This will hide it from public view.')) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/admin/scam-entity/${slug}?token=${token}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Entity removed');
        fetchScamEntities();
      } else {
        toast.error('Failed to remove entity');
      }
    } catch (err) {
      console.error('Failed to delete entity:', err);
      toast.error('Failed to remove entity');
    }
  };

  const updateReportStatus = async (reportId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/scam-report-v2/${reportId}?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success(`Report ${status}`);
        fetchScamReportsV2();
      } else {
        toast.error('Failed to update report');
      }
    } catch (err) {
      console.error('Failed to update report:', err);
      toast.error('Failed to update report');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Card className="bg-dark-black border-gold/20 w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gold/10 rounded-full mx-auto flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-gold" />
              </div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-400 mt-2">ActionFraudOne</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="username" className="text-white">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  className="bg-black border-gold/30 text-white mt-2"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="bg-black border-gold/30 text-white mt-2"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>

              {loginError && (
                <div className="p-3 bg-red-500/10 border border-red-500 rounded text-red-500 text-sm">
                  {loginError}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full bg-gold hover:bg-gold/90 text-black font-bold py-6"
                data-testid="admin-login-btn"
              >
                {isLoggingIn ? <Loader2 className="animate-spin" size={20} /> : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-24 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-gray-400">Digital Identity Protection Management</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Exposure Checks</p>
                    <p className="text-3xl font-bold text-white">{stats.exposure_checks}</p>
                  </div>
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-gold" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Scam Reports</p>
                    <p className="text-3xl font-bold text-white">{stats.scam_reports}</p>
                  </div>
                  <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Active Subscriptions</p>
                    <p className="text-3xl font-bold text-white">{stats.active_subscriptions}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Consultations Booked</p>
                    <p className="text-3xl font-bold text-white">{stats.consultations_booked}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Risk Breakdown */}
        {stats?.risk_breakdown && activeTab === 'overview' && (
          <Card className="bg-dark-black border-gold/20 mb-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gold" />
                Risk Level Breakdown
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                  <p className="text-3xl font-bold text-red-500">{stats.risk_breakdown.high}</p>
                  <p className="text-gray-400">High Risk</p>
                </div>
                <div className="text-center p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <p className="text-3xl font-bold text-yellow-500">{stats.risk_breakdown.medium}</p>
                  <p className="text-gray-400">Medium Risk</p>
                </div>
                <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <p className="text-3xl font-bold text-green-500">{stats.risk_breakdown.low}</p>
                  <p className="text-gray-400">Low Risk</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['overview', 'entities', 'reports', 'exposure', 'scams', 'analyses', 'subscriptions', 'consultations', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab
                  ? 'bg-gold text-black'
                  : 'bg-dark-black text-gray-400 hover:text-white border border-gold/20'
              }`}
              data-testid={`admin-tab-${tab}`}
            >
              {tab === 'settings' && <Settings size={16} />}
              {tab === 'entities' && <Database size={16} />}
              {tab === 'reports' && <Flag size={16} />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card className="bg-dark-black border-gold/20">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gold" />
                Threat Intelligence Settings
              </h3>

              {settingsLoading && !threatIntelSettings ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gold" />
                </div>
              ) : threatIntelSettings ? (
                <div className="space-y-8">
                  {/* API Keys Status */}
                  <div className="p-4 bg-black rounded-lg border border-gold/20">
                    <h4 className="text-gold font-semibold mb-4">API Keys Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        {threatIntelSettings.api_keys_configured?.google_webrisk ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="text-gray-300">Google Web Risk</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {threatIntelSettings.api_keys_configured?.virustotal ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="text-gray-300">VirusTotal</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {threatIntelSettings.api_keys_configured?.urlscan ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="text-gray-300">urlscan.io</span>
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mt-4">
                      API keys are configured via environment variables (GOOGLE_WEBRISK_API_KEY, VIRUSTOTAL_API_KEY, URLSCAN_API_KEY)
                    </p>
                  </div>

                  {/* Enable/Disable Integrations */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white">Google Web Risk</Label>
                      <Select
                        value={threatIntelSettings.google_webrisk_enabled ? 'enabled' : 'disabled'}
                        onValueChange={(v) => setThreatIntelSettings({...threatIntelSettings, google_webrisk_enabled: v === 'enabled'})}
                      >
                        <SelectTrigger className="bg-black border-gold/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-gold/30">
                          <SelectItem value="enabled">Enabled</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">VirusTotal</Label>
                      <Select
                        value={threatIntelSettings.virustotal_enabled ? 'enabled' : 'disabled'}
                        onValueChange={(v) => setThreatIntelSettings({...threatIntelSettings, virustotal_enabled: v === 'enabled'})}
                      >
                        <SelectTrigger className="bg-black border-gold/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-gold/30">
                          <SelectItem value="enabled">Enabled</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">urlscan.io</Label>
                      <Select
                        value={threatIntelSettings.urlscan_enabled ? 'enabled' : 'disabled'}
                        onValueChange={(v) => setThreatIntelSettings({...threatIntelSettings, urlscan_enabled: v === 'enabled'})}
                      >
                        <SelectTrigger className="bg-black border-gold/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-gold/30">
                          <SelectItem value="enabled">Enabled</SelectItem>
                          <SelectItem value="disabled">Disabled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* urlscan Visibility */}
                  <div className="space-y-2">
                    <Label className="text-white">urlscan.io Scan Visibility</Label>
                    <Select
                      value={threatIntelSettings.urlscan_visibility}
                      onValueChange={(v) => setThreatIntelSettings({...threatIntelSettings, urlscan_visibility: v})}
                    >
                      <SelectTrigger className="bg-black border-gold/30 text-white max-w-md">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-gold/30">
                        <SelectItem value="private">Private (requires paid plan)</SelectItem>
                        <SelectItem value="unlisted">Unlisted (recommended)</SelectItem>
                        <SelectItem value="public">Public</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-gray-500 text-sm">Controls visibility of submitted URL scans</p>
                  </div>

                  {/* Score Thresholds */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white">Google Web Risk Score</Label>
                      <Input
                        type="number"
                        value={threatIntelSettings.google_webrisk_score}
                        onChange={(e) => setThreatIntelSettings({...threatIntelSettings, google_webrisk_score: parseInt(e.target.value)})}
                        className="bg-black border-gold/30 text-white"
                      />
                      <p className="text-gray-500 text-xs">Points added when flagged</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">VirusTotal Score</Label>
                      <Input
                        type="number"
                        value={threatIntelSettings.virustotal_score}
                        onChange={(e) => setThreatIntelSettings({...threatIntelSettings, virustotal_score: parseInt(e.target.value)})}
                        className="bg-black border-gold/30 text-white"
                      />
                      <p className="text-gray-500 text-xs">Points added when flagged</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">urlscan Score</Label>
                      <Input
                        type="number"
                        value={threatIntelSettings.urlscan_score}
                        onChange={(e) => setThreatIntelSettings({...threatIntelSettings, urlscan_score: parseInt(e.target.value)})}
                        className="bg-black border-gold/30 text-white"
                      />
                      <p className="text-gray-500 text-xs">Points added when flagged</p>
                    </div>
                  </div>

                  {/* VirusTotal Thresholds */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-white">VirusTotal Malicious Threshold</Label>
                      <Input
                        type="number"
                        value={threatIntelSettings.virustotal_malicious_threshold}
                        onChange={(e) => setThreatIntelSettings({...threatIntelSettings, virustotal_malicious_threshold: parseInt(e.target.value)})}
                        className="bg-black border-gold/30 text-white"
                      />
                      <p className="text-gray-500 text-xs">Flag if malicious detections ≥ this value</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-white">VirusTotal Suspicious Threshold</Label>
                      <Input
                        type="number"
                        value={threatIntelSettings.virustotal_suspicious_threshold}
                        onChange={(e) => setThreatIntelSettings({...threatIntelSettings, virustotal_suspicious_threshold: parseInt(e.target.value)})}
                        className="bg-black border-gold/30 text-white"
                      />
                      <p className="text-gray-500 text-xs">Flag if suspicious detections ≥ this value</p>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center gap-4 pt-4 border-t border-gold/20">
                    <Button
                      onClick={saveThreatIntelSettings}
                      disabled={settingsLoading}
                      className="bg-gold hover:bg-gold/90 text-black font-bold"
                    >
                      {settingsLoading ? <Loader2 className="animate-spin" size={20} /> : 'Save Settings'}
                    </Button>
                    {settingsSaved && (
                      <span className="text-green-500 flex items-center gap-2">
                        <CheckCircle size={18} />
                        Settings saved successfully
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Failed to load settings</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Scam Entities Tab */}
        {activeTab === 'entities' && (
          <div className="space-y-6">
            {/* Entity Stats */}
            {entityStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-dark-black border-gold/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-gold">{entityStats.total_entities}</p>
                    <p className="text-gray-400 text-sm">Total Entities</p>
                  </CardContent>
                </Card>
                <Card className="bg-dark-black border-red-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-red-500">{entityStats.high_risk_count}</p>
                    <p className="text-gray-400 text-sm">High Risk</p>
                  </CardContent>
                </Card>
                <Card className="bg-dark-black border-blue-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-500">{entityStats.total_reports}</p>
                    <p className="text-gray-400 text-sm">User Reports</p>
                  </CardContent>
                </Card>
                <Card className="bg-dark-black border-green-500/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-500">{scamEntities.filter(e => e.is_published).length}</p>
                    <p className="text-gray-400 text-sm">Published Pages</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filter */}
            <div className="flex items-center gap-4">
              <Label className="text-white">Filter by Status:</Label>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="bg-black border-gold/30 text-white w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black border-gold/30">
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="auto_generated">Auto Generated</SelectItem>
                  <SelectItem value="verified">Verified Scams</SelectItem>
                  <SelectItem value="disputed">Disputed</SelectItem>
                  <SelectItem value="removed">Removed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Entities Table */}
            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5 text-gold" />
                  Scam Intelligence Entities ({scamEntities.length})
                </h3>

                {entitiesLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gold" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">Entity</th>
                          <th className="text-left py-3 px-4 text-gold">Type</th>
                          <th className="text-left py-3 px-4 text-gold">Risk</th>
                          <th className="text-left py-3 px-4 text-gold">Scans</th>
                          <th className="text-left py-3 px-4 text-gold">Reports</th>
                          <th className="text-left py-3 px-4 text-gold">Status</th>
                          <th className="text-left py-3 px-4 text-gold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scamEntities.map((entity, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4">
                              <div className="flex flex-col">
                                <span className="text-white font-medium">{entity.name?.slice(0, 30)}{entity.name?.length > 30 ? '...' : ''}</span>
                                <a 
                                  href={`/scam-check/${entity.slug}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-gold/60 text-xs hover:text-gold"
                                >
                                  /scam-check/{entity.slug}
                                </a>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-300 text-sm">{entity.type_label || entity.type}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                entity.risk_level === 'High Risk' ? 'bg-red-500/20 text-red-500' :
                                entity.risk_level === 'Moderate Risk' ? 'bg-orange-500/20 text-orange-500' :
                                entity.risk_level === 'Needs Expert Review' ? 'bg-yellow-500/20 text-yellow-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {entity.risk_score}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{entity.scan_count || 0}</td>
                            <td className="py-3 px-4 text-gray-300">{entity.report_count || 0}</td>
                            <td className="py-3 px-4">
                              <div className="flex flex-col gap-1">
                                {entity.is_published ? (
                                  <span className="text-green-500 text-xs flex items-center gap-1">
                                    <Eye size={12} /> Published
                                  </span>
                                ) : (
                                  <span className="text-gray-500 text-xs flex items-center gap-1">
                                    <EyeOff size={12} /> Hidden
                                  </span>
                                )}
                                {entity.verified_scam && (
                                  <span className="text-red-500 text-xs font-bold">VERIFIED</span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => togglePublished(entity)}
                                  className={`p-1.5 rounded ${entity.is_published ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'} hover:opacity-80`}
                                  title={entity.is_published ? 'Unpublish' : 'Publish'}
                                >
                                  {entity.is_published ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                                <button
                                  onClick={() => toggleVerifiedScam(entity)}
                                  className={`p-1.5 rounded ${entity.verified_scam ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'} hover:opacity-80`}
                                  title={entity.verified_scam ? 'Remove Verified' : 'Mark as Verified Scam'}
                                >
                                  {entity.verified_scam ? <XCircle size={14} /> : <CheckCircle size={14} />}
                                </button>
                                <button
                                  onClick={() => setEditingEntity(entity)}
                                  className="p-1.5 rounded bg-gold/20 text-gold hover:opacity-80"
                                  title="Edit"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => deleteEntity(entity.slug)}
                                  className="p-1.5 rounded bg-red-500/20 text-red-500 hover:opacity-80"
                                  title="Remove"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {scamEntities.length === 0 && (
                      <p className="text-center text-gray-400 py-8">No entities found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Edit Entity Modal */}
            {editingEntity && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                <Card className="bg-dark-black border-gold/30 w-full max-w-lg">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-white mb-4">Edit Entity</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-300">Entity Name</Label>
                        <p className="text-white font-medium">{editingEntity.name}</p>
                      </div>
                      <div>
                        <Label className="text-gray-300">Meta Title</Label>
                        <Input
                          value={editingEntity.meta_title || ''}
                          onChange={(e) => setEditingEntity({...editingEntity, meta_title: e.target.value})}
                          className="bg-black border-gold/30 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Meta Description</Label>
                        <Textarea
                          value={editingEntity.meta_description || ''}
                          onChange={(e) => setEditingEntity({...editingEntity, meta_description: e.target.value})}
                          className="bg-black border-gold/30 text-white mt-1 min-h-[80px]"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Admin Notes</Label>
                        <Textarea
                          value={editingEntity.admin_notes || ''}
                          onChange={(e) => setEditingEntity({...editingEntity, admin_notes: e.target.value})}
                          className="bg-black border-gold/30 text-white mt-1 min-h-[60px]"
                          placeholder="Internal notes about this entity..."
                        />
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button
                          onClick={() => updateEntity(editingEntity.slug, {
                            meta_title: editingEntity.meta_title,
                            meta_description: editingEntity.meta_description,
                            admin_notes: editingEntity.admin_notes
                          })}
                          className="bg-gold hover:bg-gold/90 text-black font-bold"
                        >
                          Save Changes
                        </Button>
                        <Button
                          onClick={() => setEditingEntity(null)}
                          variant="outline"
                          className="border-gray-600 text-gray-300"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Scam Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Filter */}
            <div className="flex items-center gap-4">
              <Label className="text-white">Filter by Status:</Label>
              <Select value={reportFilter} onValueChange={setReportFilter}>
                <SelectTrigger className="bg-black border-gold/30 text-white w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black border-gold/30">
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reports Table */}
            <Card className="bg-dark-black border-gold/20">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-gold" />
                  User Reports ({scamReportsV2.length})
                </h3>

                {reportsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gold" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">Entity</th>
                          <th className="text-left py-3 px-4 text-gold">Scam Type</th>
                          <th className="text-left py-3 px-4 text-gold">Description</th>
                          <th className="text-left py-3 px-4 text-gold">Country</th>
                          <th className="text-left py-3 px-4 text-gold">Amount</th>
                          <th className="text-left py-3 px-4 text-gold">Date</th>
                          <th className="text-left py-3 px-4 text-gold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {scamReportsV2.map((report, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4">
                              <div className="flex flex-col">
                                <span className="text-white">{report.entity_name?.slice(0, 25)}...</span>
                                <span className="text-gray-500 text-xs">{report.entity_type}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 rounded text-xs font-medium bg-gold/20 text-gold">
                                {report.scam_type}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-300 text-sm max-w-xs truncate">
                              {report.description?.slice(0, 80)}...
                            </td>
                            <td className="py-3 px-4 text-gray-300">{report.country || '-'}</td>
                            <td className="py-3 px-4 text-gray-300">{report.amount_lost || '-'}</td>
                            <td className="py-3 px-4 text-gray-400 text-sm">
                              {report.reported_at ? new Date(report.reported_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-3 px-4">
                              {reportFilter === 'pending' ? (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateReportStatus(report.report_id, 'approved')}
                                    className="p-1.5 rounded bg-green-500/20 text-green-500 hover:opacity-80"
                                    title="Approve"
                                  >
                                    <CheckCircle size={14} />
                                  </button>
                                  <button
                                    onClick={() => updateReportStatus(report.report_id, 'rejected')}
                                    className="p-1.5 rounded bg-red-500/20 text-red-500 hover:opacity-80"
                                    title="Reject"
                                  >
                                    <XCircle size={14} />
                                  </button>
                                </div>
                              ) : (
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  report.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {report.status?.toUpperCase()}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {scamReportsV2.length === 0 && (
                      <p className="text-center text-gray-400 py-8">No reports found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Data Tables */}
        {activeTab !== 'overview' && activeTab !== 'settings' && (
          <Card className="bg-dark-black border-gold/20">
            <CardContent className="p-6">
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gold" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {activeTab === 'exposure' && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">ID</th>
                          <th className="text-left py-3 px-4 text-gold">Risk Level</th>
                          <th className="text-left py-3 px-4 text-gold">Score</th>
                          <th className="text-left py-3 px-4 text-gold">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.exposure_checks.map((item, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4 text-gray-300">{item.id?.slice(0, 8)}...</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                item.risk_level === 'high' ? 'bg-red-500/20 text-red-500' :
                                item.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {item.risk_level?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{item.risk_score}/100</td>
                            <td className="py-3 px-4 text-gray-400">{new Date(item.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {activeTab === 'scams' && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">Type</th>
                          <th className="text-left py-3 px-4 text-gold">Location</th>
                          <th className="text-left py-3 px-4 text-gold">Status</th>
                          <th className="text-left py-3 px-4 text-gold">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.scam_reports.map((item, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4 text-gray-300">{item.scam_type}</td>
                            <td className="py-3 px-4 text-gray-300">{item.location || '-'}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-500/20 text-yellow-500">
                                {item.status?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-400">{new Date(item.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {activeTab === 'analyses' && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">Risk Level</th>
                          <th className="text-left py-3 px-4 text-gold">Score</th>
                          <th className="text-left py-3 px-4 text-gold">URLs Found</th>
                          <th className="text-left py-3 px-4 text-gold">External Checks</th>
                          <th className="text-left py-3 px-4 text-gold">Flags</th>
                          <th className="text-left py-3 px-4 text-gold">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.scam_analyses.map((item, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                item.risk_level === 'high' ? 'bg-red-500/20 text-red-500' :
                                item.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {item.risk_level?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-300">{item.risk_score}/100</td>
                            <td className="py-3 px-4 text-gray-300">{item.urls_found || 0}</td>
                            <td className="py-3 px-4">
                              {item.external_checks_available ? (
                                <span className="text-green-500">✓</span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-300 text-xs">
                              {item.flags?.google_webrisk && <span className="mr-2 text-blue-400">G</span>}
                              {item.flags?.virustotal && <span className="mr-2 text-purple-400">VT</span>}
                              {item.flags?.urlscan && <span className="mr-2 text-cyan-400">US</span>}
                              {item.flags?.heuristic_indicators > 0 && <span className="text-gold">H:{item.flags.heuristic_indicators}</span>}
                            </td>
                            <td className="py-3 px-4 text-gray-400">{new Date(item.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {(activeTab === 'subscriptions' || activeTab === 'consultations') && (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gold/20">
                          <th className="text-left py-3 px-4 text-gold">Email</th>
                          <th className="text-left py-3 px-4 text-gold">Product</th>
                          <th className="text-left py-3 px-4 text-gold">Amount</th>
                          <th className="text-left py-3 px-4 text-gold">Status</th>
                          <th className="text-left py-3 px-4 text-gold">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data[activeTab].map((item, index) => (
                          <tr key={index} className="border-b border-gold/10 hover:bg-gold/5">
                            <td className="py-3 px-4 text-gray-300">{item.email}</td>
                            <td className="py-3 px-4 text-gray-300">{item.product_id}</td>
                            <td className="py-3 px-4 text-gold">£{item.amount}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                item.payment_status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                                item.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                'bg-red-500/20 text-red-500'
                              }`}>
                                {item.payment_status?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-400">{new Date(item.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {data[activeTab === 'exposure' ? 'exposure_checks' : 
                       activeTab === 'scams' ? 'scam_reports' : 
                       activeTab === 'analyses' ? 'scam_analyses' : activeTab]?.length === 0 && (
                    <p className="text-center text-gray-400 py-8">No data available</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
